/**
 * Support ticket store — localStorage-first, mirrored to Supabase
 * `support_tickets` and `support_messages` when configured.
 *
 * Required Supabase SQL (run once):
 *
 *   CREATE TABLE IF NOT EXISTS support_tickets (
 *       id            TEXT PRIMARY KEY,
 *       user_id       TEXT NOT NULL,
 *       user_email    TEXT,
 *       user_name     TEXT,
 *       category      TEXT NOT NULL,
 *       subject       TEXT NOT NULL,
 *       message       TEXT NOT NULL,
 *       attachment    TEXT,
 *       status        TEXT NOT NULL DEFAULT 'open',
 *       created_at    TIMESTAMPTZ DEFAULT now(),
 *       updated_at    TIMESTAMPTZ DEFAULT now()
 *   );
 *
 *   CREATE TABLE IF NOT EXISTS support_messages (
 *       id           TEXT PRIMARY KEY,
 *       ticket_id    TEXT NOT NULL REFERENCES support_tickets(id) ON DELETE CASCADE,
 *       author_id    TEXT NOT NULL,
 *       author_role  TEXT NOT NULL CHECK (author_role IN ('user','admin')),
 *       body         TEXT NOT NULL,
 *       created_at   TIMESTAMPTZ DEFAULT now()
 *   );
 */
import { mirror } from "@/lib/syncStore";

const TICKETS_KEY = "neyra.supportTickets";
const MESSAGES_KEY = "neyra.supportMessages";

export const TICKET_STATUS = {
    OPEN: "open",
    PENDING: "pending",
    RESOLVED: "resolved",
    CLOSED: "closed",
};

export const TICKET_CATEGORIES = [
    "Account",
    "Payment / Billing",
    "Verification",
    "Booking issue",
    "Service / Event report",
    "Bug or app issue",
    "Other",
];

const SUPPORT_TICKETS_TABLE = "support_tickets";
const SUPPORT_MESSAGES_TABLE = "support_messages";

const read = (key, fallback) => {
    try {
        const raw = JSON.parse(localStorage.getItem(key) || "null");
        return Array.isArray(raw) ? raw : fallback;
    } catch { return fallback; }
};
const write = (key, val) => {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* quota */ }
};

const fireMirror = (table, row) => {
    try { mirror(table, row); } catch { /* ignore */ }
};

const genId = (prefix) =>
    `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

/* ---------- Tickets ---------- */

export const getAllTickets = () => read(TICKETS_KEY, []);

export const getUserTickets = (userId) =>
    getAllTickets()
        .filter((t) => t.user_id === userId)
        .sort((a, b) => Date.parse(b.updated_at || 0) - Date.parse(a.updated_at || 0));

export const getTicketById = (id) =>
    getAllTickets().find((t) => t.id === id) || null;

export const createTicket = ({
    user, category, subject, message, attachment = null,
}) => {
    if (!user || !category || !subject?.trim() || !message?.trim()) {
        return { ok: false, error: "All fields are required." };
    }
    const now = new Date().toISOString();
    const ticket = {
        id: genId("tkt"),
        user_id: user.id,
        user_email: user.email || null,
        user_name: user.fullName || user.username || null,
        category,
        subject: subject.trim(),
        message: message.trim(),
        attachment: attachment || null,
        status: TICKET_STATUS.OPEN,
        created_at: now,
        updated_at: now,
    };
    const list = getAllTickets();
    list.unshift(ticket);
    write(TICKETS_KEY, list);
    fireMirror(SUPPORT_TICKETS_TABLE, ticket);

    // Seed first message thread entry from the user's body.
    const seed = {
        id: genId("msg"),
        ticket_id: ticket.id,
        author_id: user.id,
        author_role: "user",
        body: ticket.message,
        created_at: now,
    };
    const msgs = read(MESSAGES_KEY, []);
    msgs.push(seed);
    write(MESSAGES_KEY, msgs);
    fireMirror(SUPPORT_MESSAGES_TABLE, seed);

    return { ok: true, ticket };
};

export const updateTicketStatus = (id, status) => {
    const list = getAllTickets();
    const idx = list.findIndex((t) => t.id === id);
    if (idx < 0) return null;
    list[idx] = { ...list[idx], status, updated_at: new Date().toISOString() };
    write(TICKETS_KEY, list);
    fireMirror(SUPPORT_TICKETS_TABLE, list[idx]);
    return list[idx];
};

/* ---------- Messages ---------- */

export const getTicketMessages = (ticketId) =>
    read(MESSAGES_KEY, [])
        .filter((m) => m.ticket_id === ticketId)
        .sort((a, b) => Date.parse(a.created_at || 0) - Date.parse(b.created_at || 0));

export const replyToTicket = ({ ticket, author, role, body }) => {
    if (!ticket || !author || !body?.trim()) {
        return { ok: false, error: "Empty reply." };
    }
    const now = new Date().toISOString();
    const msg = {
        id: genId("msg"),
        ticket_id: ticket.id,
        author_id: author.id,
        author_role: role === "admin" ? "admin" : "user",
        body: body.trim(),
        created_at: now,
    };
    const msgs = read(MESSAGES_KEY, []);
    msgs.push(msg);
    write(MESSAGES_KEY, msgs);
    fireMirror(SUPPORT_MESSAGES_TABLE, msg);

    // Bump the ticket's status: an admin reply moves OPEN → PENDING; a user
    // reply on a PENDING ticket moves it back to OPEN.
    const tickets = getAllTickets();
    const idx = tickets.findIndex((t) => t.id === ticket.id);
    if (idx >= 0) {
        let nextStatus = tickets[idx].status;
        if (role === "admin" && nextStatus === TICKET_STATUS.OPEN) {
            nextStatus = TICKET_STATUS.PENDING;
        } else if (role === "user" && nextStatus === TICKET_STATUS.PENDING) {
            nextStatus = TICKET_STATUS.OPEN;
        }
        tickets[idx] = { ...tickets[idx], status: nextStatus, updated_at: now };
        write(TICKETS_KEY, tickets);
        fireMirror(SUPPORT_TICKETS_TABLE, tickets[idx]);
    }
    return { ok: true, message: msg };
};
